/** @file The renderer's page table: loaded identity pages or 1x1 stand-ins, then white and font (R-E1-16). */
import type { SkeletonData } from '@pose2d/core/types';
import { createTexturePages } from '@pose2d/geometry/createTexturePages';
import type { TexturePages } from '@pose2d/geometry/types';
import type { RgbaImage } from '@pose2d/math/types';
import type { FontAtlas } from '@pose2d/overlay/types';

/** Pages and their premultiplied images in TexturePages order; `missing` counts pages that failed to load. */
export interface PageSet { textures: TexturePages; images: RgbaImage[]; missing: number }

const WHITE: RgbaImage = { width: 1, height: 1, data: Uint8ClampedArray.of(255, 255, 255, 255), premultiplied: true };
const BLANK: RgbaImage = { width: 1, height: 1, data: new Uint8ClampedArray(4), premultiplied: true };

/** `loaded`: sha256 -> image, null = failed, absent = still loading. */
export function pageSet(data: SkeletonData, loaded: ReadonlyMap<string, RgbaImage | null>, font: FontAtlas, fontImage: RgbaImage): PageSet {
  const base = createTexturePages(data, { w: font.pageW, h: font.pageH });
  const images: RgbaImage[] = [];
  let missing = 0;
  const pages = base.pages.map((page, i) => {
    const image = i === base.white ? WHITE : i === base.font ? fontImage : (loaded.get(page.id) ?? null);
    if (image !== null && image.width === page.w && image.height === page.h) {
      images.push(image);
      return page;
    }
    if (loaded.has(page.id)) missing += 1;
    images.push(BLANK);
    return { ...page, w: 1, h: 1 };
  });
  return { textures: { ...base, pages }, images, missing };
}
